import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = ["#000000", "#4B5563", "#9CA3AF", "#D1D5DB", "#E5E7EB"];

function Overview() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [instructors, setInstructors] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [payments, setPayments] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [reports, setReports] = useState([]);

  // Helper function to parse dates
  const parseDate = useCallback((dateStr) => {
    if (!dateStr) return new Date(0);
    if (dateStr.includes("/")) {
      const parts = dateStr.split("/");
      if (parts.length === 3) {
        return new Date(
          parseInt(parts[2]),
          parseInt(parts[1]) - 1,
          parseInt(parts[0])
        );
      }
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? new Date(0) : parsed;
  }, []);

  // Fetch all data
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // Fetch instructors
        const instructorsSnapshot = await getDocs(collection(db, "instructors"));
        const instructorsData = instructorsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setInstructors(instructorsData);

        // Fetch candidates
        const candidatesSnapshot = await getDocs(collection(db, "candidates"));
        const candidatesData = candidatesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCandidates(candidatesData);

        // Fetch payments
        const paymentsSnapshot = await getDocs(collection(db, "payments"));
        const paymentsData = paymentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPayments(paymentsData);

        // Fetch lessons
        const lessonsSnapshot = await getDocs(collection(db, "lessons"));
        const lessonsData = lessonsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLessons(lessonsData);

        // Fetch reports
        const reportsSnapshot = await getDocs(collection(db, "reports"));
        const reportsData = reportsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setReports(reportsData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Calculate statistics
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisYear = new Date(now.getFullYear(), 0, 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    // Total counts
    const totalInstructors = instructors.length;
    const totalCandidates = candidates.length;
    const totalLessons = lessons.length;
    const totalReports = reports.length;

    // Active counts
    const activeInstructors = instructors.filter(
      (i) => i.status === "active"
    ).length;
    const activeCandidates = candidates.filter(
      (c) => c.status === "active"
    ).length;

    // Payments statistics
    const incomePayments = payments.filter((p) => p.type === "income");
    const outcomePayments = payments.filter((p) => p.type === "outcome");
    const totalIncome = incomePayments.reduce(
      (sum, p) => sum + (parseFloat(p.amount) || 0),
      0
    );
    const totalOutcome = outcomePayments.reduce(
      (sum, p) => sum + (parseFloat(p.amount) || 0),
      0
    );
    const netRevenue = totalIncome - totalOutcome;

    // This month payments
    const thisMonthIncome = incomePayments
      .filter((p) => {
        if (!p.date) return false;
        const paymentDate = parseDate(p.date);
        return paymentDate >= thisMonth && paymentDate <= now;
      })
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    const thisMonthOutcome = outcomePayments
      .filter((p) => {
        if (!p.date) return false;
        const paymentDate = parseDate(p.date);
        return paymentDate >= thisMonth && paymentDate <= now;
      })
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    // Last month payments for comparison
    const lastMonthIncome = incomePayments
      .filter((p) => {
        if (!p.date) return false;
        const paymentDate = parseDate(p.date);
        return paymentDate >= lastMonth && paymentDate <= lastMonthEnd;
      })
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    // Total hours
    const totalHours = lessons.reduce(
      (sum, l) => sum + (parseFloat(l.hours) || 0),
      0
    );

    // This month hours
    const thisMonthHours = lessons
      .filter((l) => {
        if (!l.date) return false;
        const lessonDate = parseDate(l.date);
        return lessonDate >= thisMonth && lessonDate <= now;
      })
      .reduce((sum, l) => sum + (parseFloat(l.hours) || 0), 0);

    // Gender distribution
    const maleCandidates = candidates.filter((c) => c.gender === "male").length;
    const femaleCandidates = candidates.filter(
      (c) => c.gender === "female"
    ).length;

    // Status distribution
    const activeStatus = candidates.filter((c) => c.status === "active").length;
    const completedStatus = candidates.filter(
      (c) => c.status === "completed"
    ).length;
    const pausedStatus = candidates.filter((c) => c.status === "paused").length;
    const failedStatus = candidates.filter((c) => c.status === "failed").length;

    // Category distribution
    const categoryCounts = {};
    candidates.forEach((c) => {
      const category = c.category || "Unknown";
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    return {
      totalInstructors,
      activeInstructors,
      totalCandidates,
      activeCandidates,
      totalLessons,
      totalHours,
      thisMonthHours,
      totalReports,
      totalIncome,
      totalOutcome,
      netRevenue,
      thisMonthIncome,
      thisMonthOutcome,
      lastMonthIncome,
      incomeGrowth:
        lastMonthIncome > 0
          ? ((thisMonthIncome - lastMonthIncome) / lastMonthIncome) * 100
          : 0,
      maleCandidates,
      femaleCandidates,
      activeStatus,
      completedStatus,
      pausedStatus,
      failedStatus,
      categoryCounts,
    };
  }, [instructors, candidates, payments, lessons, reports, parseDate]);

  // Monthly revenue data for line chart
  const monthlyRevenueData = useMemo(() => {
    const months = [];
    const now = new Date();
    
    // Get last 12 months
    for (let i = 11; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(
        now.getFullYear(),
        now.getMonth() - i + 1,
        0,
        23,
        59,
        59
      );
      
      const monthIncome = payments
        .filter((p) => {
          if (!p.date || p.type !== "income") return false;
          const paymentDate = parseDate(p.date);
          return paymentDate >= monthDate && paymentDate <= monthEnd;
        })
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

      const monthOutcome = payments
        .filter((p) => {
          if (!p.date || p.type !== "outcome") return false;
          const paymentDate = parseDate(p.date);
          return paymentDate >= monthDate && paymentDate <= monthEnd;
        })
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

      months.push({
        month: monthDate.toLocaleDateString("en-US", { month: "short" }),
        income: monthIncome,
        outcome: monthOutcome,
        net: monthIncome - monthOutcome,
      });
    }
    
    return months;
  }, [payments, parseDate]);

  // Monthly lessons data
  const monthlyLessonsData = useMemo(() => {
    const months = [];
    const now = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(
        now.getFullYear(),
        now.getMonth() - i + 1,
        0,
        23,
        59,
        59
      );
      
      const monthHours = lessons
        .filter((l) => {
          if (!l.date) return false;
          const lessonDate = parseDate(l.date);
          return lessonDate >= monthDate && lessonDate <= monthEnd;
        })
        .reduce((sum, l) => sum + (parseFloat(l.hours) || 0), 0);

      const monthCount = lessons.filter((l) => {
        if (!l.date) return false;
        const lessonDate = parseDate(l.date);
        return lessonDate >= monthDate && lessonDate <= monthEnd;
      }).length;

      months.push({
        month: monthDate.toLocaleDateString("en-US", { month: "short" }),
        hours: monthHours,
        count: monthCount,
      });
    }
    
    return months;
  }, [lessons, parseDate]);

  // Gender distribution data
  const genderData = [
    { name: t("overview.male"), value: stats.maleCandidates },
    { name: t("overview.female"), value: stats.femaleCandidates },
  ];

  // Status distribution data
  const statusData = [
    { name: t("overview.active"), value: stats.activeStatus },
    { name: t("overview.completed"), value: stats.completedStatus },
    { name: t("overview.paused"), value: stats.pausedStatus },
    { name: t("overview.failed"), value: stats.failedStatus },
  ];

  // Category distribution data
  const categoryData = Object.entries(stats.categoryCounts).map(
    ([name, value]) => ({
      name,
      value,
    })
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t("overview.title")}
          </h2>
          <p className="text-gray-600">{t("overview.subtitle")}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Instructors Card */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {t("overview.totalInstructors")}
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {stats.totalInstructors}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.activeInstructors} {t("overview.active")}
                </p>
              </div>
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Candidates Card */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {t("overview.totalCandidates")}
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {stats.totalCandidates}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.activeCandidates} {t("overview.active")}
                </p>
              </div>
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Total Hours Card */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {t("overview.totalHours")}
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {Math.round(stats.totalHours)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {Math.round(stats.thisMonthHours)} {t("overview.thisMonth")}
                </p>
              </div>
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Revenue Card */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {t("overview.netRevenue")}
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  €{Math.round(stats.netRevenue)}
                </p>
                <p
                  className={`text-xs mt-1 ${
                    stats.incomeGrowth >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {stats.incomeGrowth >= 0 ? "+" : ""}
                  {Math.round(stats.incomeGrowth)}% {t("overview.vsLastMonth")}
                </p>
              </div>
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Revenue Chart */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("overview.monthlyRevenue")}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip
                  formatter={(value) => `€${Math.round(value)}`}
                  contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e7eb" }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="income"
                  stroke="#000000"
                  strokeWidth={2}
                  name={t("overview.income")}
                />
                <Line
                  type="monotone"
                  dataKey="outcome"
                  stroke="#EF4444"
                  strokeWidth={2}
                  name={t("overview.outcome")}
                />
                <Line
                  type="monotone"
                  dataKey="net"
                  stroke="#10B981"
                  strokeWidth={2}
                  name={t("overview.net")}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly Lessons Chart */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("overview.monthlyLessons")}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyLessonsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip
                  contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e7eb" }}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="hours"
                  fill="#000000"
                  name={t("overview.hours")}
                />
                <Bar
                  yAxisId="right"
                  dataKey="count"
                  fill="#4B5563"
                  name={t("overview.lessons")}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Gender Distribution */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("overview.genderDistribution")}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={genderData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {genderData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Status Distribution */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("overview.statusDistribution")}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Category Distribution */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("overview.categoryDistribution")}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={categoryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#000000" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <p className="text-sm font-medium text-gray-600">
              {t("overview.totalIncome")}
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              €{Math.round(stats.totalIncome)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t("overview.thisMonth")}: €{Math.round(stats.thisMonthIncome)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <p className="text-sm font-medium text-gray-600">
              {t("overview.totalOutcome")}
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              €{Math.round(stats.totalOutcome)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t("overview.thisMonth")}: €{Math.round(stats.thisMonthOutcome)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <p className="text-sm font-medium text-gray-600">
              {t("overview.totalReports")}
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {stats.totalReports}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t("overview.totalLessons")}: {stats.totalLessons}
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Overview;
